import React, { useState } from 'react';

// Hooks
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Validation libraries
import { useFormik } from 'formik';
import * as yup from 'yup';
import { validateForm, realTimeValidation, resetValidation } from '~/utils/formik-validation';

// Actions
import { createTemplateCategory } from '~/redux/actions/admin-templates-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';

// Utils
import { MESSAGES } from '~/utils/message';
import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

// MUI Components
import Typography from '@mui/material/Typography';
import { Box, InputLabel, TextField, CircularProgress } from '@mui/material';

const CreateCategoryModal = (props) => {
  const { openModal, handleCloseModal, pagination } = props;
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  const validationSchema = yup.object().shape({
    name: yup
      .string()
      .trim()
      .required(MESSAGES.ADMIN_DASHBOARD.CATEGORIES.CREATE.CATEGORY_NAME_REQUIRED)
      .min(1, MESSAGES.ADMIN_DASHBOARD.CATEGORIES.CREATE.CATEGORY_NAME_REQUIRED)
      .max(255, MESSAGES.ADMIN_DASHBOARD.CATEGORIES.CREATE.CATEGORY_NAME_LIMIT),
    description: yup
      .string()
      .trim()
      .nullable()
      .max(255, MESSAGES.ADMIN_DASHBOARD.CATEGORIES.CREATE.CATEGORY_DESC_LIMIT),
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
    },
    validationSchema: validationSchema,
  });

  const onClose = () => {
    handleCloseModal();
    formik.handleReset();
    resetValidation();
  };

  const navigate = useNavigate();

  const createNewTemplateCategory = async (event) => {
    try {
      event.preventDefault();
      const isValid = validateForm(formik);
      if (!isValid) {
        return;
      }
      setLoading(true);
      const payload = {
        title: formik.values.name,
        description: formik.values.description,
      };
      const response = await dispatch(createTemplateCategory(payload, pagination));
      dispatch(success(response.data.message));
      onClose();
      navigate('/admin/categories');
    } catch (error) {
      dispatch(failure(error.response.data.message));
      return error;
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  return (
    <OlcFormModal
      open={openModal}
      onClose={onClose}
      title={MESSAGES.ADMIN_DASHBOARD.CATEGORIES.CREATE.TITLE}
      className="olcAdminFormModal olcCategoryFormModal olcNoPersistentScrollbar"
      footer={
        <>
          <OlcButton variant="secondary" type="button" onClick={onClose}>
            {MESSAGES.ADMIN_DASHBOARD.CATEGORIES.CANCEL_BUTTON}
          </OlcButton>
          <OlcButton
            variant="primary"
            type="submit"
            form="create-template-category-form"
            disabled={loading}
          >
            {loading ? (
              <CircularProgress
                sx={{
                  color: 'white',
                  width: '25px !important',
                  height: '25px !important',
                }}
              />
            ) : (
              MESSAGES.ADMIN_DASHBOARD.CATEGORIES.SUBMIT_BUTTON
            )}
          </OlcButton>
        </>
      }
    >
      <form
        id="create-template-category-form"
        className="olcAdminFormFields"
        onSubmit={createNewTemplateCategory}
      >
        <Box className="olcAdminField">
          <InputLabel htmlFor="Name" className="inputLabel">
            {MESSAGES.ADMIN_DASHBOARD.CATEGORIES.CREATE.NAME_LABEL}
          </InputLabel>
          <TextField
            placeholder={MESSAGES.ADMIN_DASHBOARD.CATEGORIES.CREATE.NAME_PLACEHOLDER}
            fullWidth
            autoComplete="off"
            name="name"
            className={`olcAdminTextField ${
              formik.errors.name && formik.touched.name ? 'invalid' : ''
            }`}
            sx={{
              '& input::placeholder': {
                color: '#000',
                fontSize: '14px',
                fontWeight: '400',
                lineHeight: 'normal',
                fontFamily: 'Inter',
                fontStyle: 'normal',
              },
              outline: 'none',
              '&:focus': {
                outline: 'none',
              },
            }}
            onChange={formik.handleChange}
            value={formik.values.name.trimStart()}
            onBlur={realTimeValidation ? formik.handleBlur : undefined}
          />
          {formik.errors.name ? (
            <Typography className="errorMessage">
              <sup>*</sup>
              {formik.errors.name}
            </Typography>
          ) : null}
        </Box>
        <Box className="olcAdminField">
          <InputLabel htmlFor="Name" className="inputLabel">
            {MESSAGES.ADMIN_DASHBOARD.CATEGORIES.CREATE.DESCRIPTION_LABEL}
          </InputLabel>
          <TextField
            placeholder={MESSAGES.ADMIN_DASHBOARD.CATEGORIES.CREATE.DESCRIPTION_PLACEHOLDER}
            fullWidth
            autoComplete="off"
            name="description"
            className={`olcAdminTextField ${
              formik.errors.discription && formik.touched.discription ? 'invalid' : ''
            }`}
            sx={{
              '& input::placeholder': {
                color: '#000',
                fontSize: '14px',
                fontWeight: '400',
                lineHeight: 'normal',
                fontFamily: 'Inter',
                fontStyle: 'normal',
              },
              outline: 'none',
              '&:focus': {
                outline: 'none',
              },
            }}
            onChange={formik.handleChange}
            value={formik.values.description.trimStart()}
            onBlur={realTimeValidation ? formik.handleBlur : undefined}
          />
          {formik.errors.description ? (
            <Typography className="errorMessage">
              <sup>*</sup>
              {formik.errors.description}
            </Typography>
          ) : null}
        </Box>
      </form>
    </OlcFormModal>
  );
};

export default CreateCategoryModal;
